/**
 * Euchre game view.
 *
 * Reuses CircleTable, Hand, PlayingCard. Game-specific bits:
 *   - up-card display during bidding,
 *   - 2-round bidding action bar (order up + alone, or call-other-suit),
 *   - dealer-discard action bar,
 *   - trump indicator + bower decorator on the player's hand.
 */

import { useMemo, useState } from "react";
import {
  lerpCardWidth,
  useDynamicTableTop,
  useResetOnChange,
  useWindowWidth,
} from "../../lib/uiHooks.js";
import type {
  EuchrePublicState,
} from "../../../../shared/games/euchre.js";
import type { CardId, SuitId } from "../../../../shared/games/shengji.js";
import type { ClientMsg, GameEvent } from "../../../../shared/protocol.js";
import { CircleTable } from "../../components/CircleTable.js";
import { Hand } from "../../components/Hand.js";
import { PlayingCard, type CardDecorator } from "../../components/PlayingCard.js";
import { EuchreScoreBar, teamColor, teamName } from "./EuchreScoreBar.js";
import { EuchreTrumpPanel } from "./EuchreTrumpPanel.js";

interface EuchreGameProps {
  state: EuchrePublicState;
  yourHand: CardId[];
  yourSeat: number | null;
  events: GameEvent[];
  send: (m: ClientMsg) => void;
  mobile?: boolean;
}

const SUIT_GLYPH: Record<string, string> = {
  C: "♣", D: "♦", H: "♥", S: "♠",
};

const SUIT_NAME: Record<string, string> = {
  C: "Clubs", D: "Diamonds", H: "Hearts", S: "Spades",
};

const SAME_COLOR: Record<string, string> = {
  C: "S", S: "C", D: "H", H: "D",
};

/** Mark the right and left bowers on the player's hand. */
function euchreDecoratorFactory(trump: SuitId | null) {
  return function decorator(id: CardId): CardDecorator {
    if (!trump) return null;
    if (id.length !== 2) return null;
    const rank = id[0]!;
    const suit = id[1]!;
    if (rank !== "J") return null;
    if (suit === trump) {
      // Right bower
      return (
        <g>
          <rect x={4} y={28} width={5} height={84} rx={2.5} fill="#d4af37" />
          <text x={6.5} y={123.5} fontSize={8} textAnchor="middle" fill="#fff" fontWeight={700}>R</text>
          <circle cx={6.5} cy={120} r={7} fill="#d4af37" />
          <text x={6.5} y={123.5} fontSize={8} textAnchor="middle" fill="#fff" fontWeight={700}>R</text>
        </g>
      );
    }
    if (suit === SAME_COLOR[trump]) {
      // Left bower
      return (
        <g>
          <rect x={4} y={28} width={5} height={84} rx={2.5} fill="#9c7912" />
          <circle cx={6.5} cy={120} r={7} fill="#9c7912" />
          <text x={6.5} y={123.5} fontSize={8} textAnchor="middle" fill="#fff" fontWeight={700}>L</text>
        </g>
      );
    }
    return null;
  };
}

export function EuchreGame({
  state,
  yourHand,
  yourSeat,
  send,
  mobile = false,
}: EuchreGameProps) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [scoresOpen, setScoresOpen] = useState(!mobile);

  const phaseKey = `${state.phase}:${state.roundNumber}:${state.currentTrickPlays.length}:${state.completedTricks.length}`;
  useResetOnChange(phaseKey, () => setSelected(new Set()));

  const windowWidth = useWindowWidth();
  const playZoneCardWidth = mobile ? 56 : lerpCardWidth(windowWidth, 90, 132);

  const legal = useMemo(() => {
    if (yourSeat === null) return new Set<CardId>();
    if (state.phase === "discard") return new Set(yourHand);
    if (state.phase !== "play") return new Set(yourHand);
    if (state.currentSeat !== yourSeat) return new Set<CardId>();
    return computeEuchreLegal(yourHand, state);
  }, [yourHand, state, yourSeat]);

  const selectedCardIds = useMemo<CardId[]>(
    () => [...selected].map((i) => yourHand[i]!).filter(Boolean),
    [selected, yourHand]
  );

  function toggle(i: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      // Single-select for play/discard.
      if (next.size > 1) {
        for (const x of [...next]) if (x !== i) next.delete(x);
      }
      return next;
    });
  }

  const decorator = useMemo(() => euchreDecoratorFactory(state.trumpSuit), [state.trumpSuit]);

  const myBidTurn =
    yourSeat !== null && state.currentSeat === yourSeat &&
    (state.phase === "bidding1" || state.phase === "bidding2");
  const myDiscardTurn =
    yourSeat !== null && state.phase === "discard" && state.currentSeat === yourSeat;
  const myPlayTurn =
    yourSeat !== null && state.phase === "play" && state.currentSeat === yourSeat;

  const tableTopRef = useDynamicTableTop();

  return (
    <div className="game-view euchre-view" ref={tableTopRef}>
      <EuchreScoreBar state={state} yourSeat={yourSeat} />
      <EuchreTrumpPanel state={state} />
      <EuchreHandProgress state={state} yourSeat={yourSeat} />

      <CircleTable
        seatCount={state.players.length}
        mySeatIndex={yourSeat}
        renderSeat={(i) => (
          <EuchreSeatView
            seat={i}
            state={state}
            yourSeat={yourSeat}
            playZoneCardWidth={playZoneCardWidth}
            decoratorOf={decorator}
          />
        )}
      >
        <EuchreCenter state={state} cardWidth={playZoneCardWidth} decoratorOf={decorator} />
      </CircleTable>

      <button
        className="scoreboard-toggle euchre-history-toggle"
        onClick={() => setScoresOpen((v) => !v)}
        aria-pressed={scoresOpen}
      >
        {scoresOpen ? "Hide history" : "Show history"}
      </button>
      {scoresOpen && <EuchreScorePanel state={state} yourSeat={yourSeat} />}

      {myBidTurn && <BidActionBar state={state} yourSeat={yourSeat!} send={send} />}
      {myDiscardTurn && (
        <DiscardActionBar
          selectedCard={selectedCardIds[0] ?? null}
          send={send}
          onAfterSubmit={() => setSelected(new Set())}
        />
      )}
      {myPlayTurn && (
        <PlayActionBar
          selectedCard={selectedCardIds[0] ?? null}
          send={send}
          onAfterSubmit={() => setSelected(new Set())}
        />
      )}
      {yourSeat !== null && state.phase === "round_end" && (
        <ReadyActionBar send={send} />
      )}

      {yourSeat !== null && (
        <Hand
          cards={yourHand}
          legal={(state.phase === "play" || state.phase === "discard") ? legal : undefined}
          selected={selected}
          onToggle={toggle}
          decoratorOf={decorator}
          mobile={mobile}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Seat view
// ---------------------------------------------------------------------------

function EuchreSeatView({
  seat,
  state,
  yourSeat,
  playZoneCardWidth,
  decoratorOf,
}: {
  seat: number;
  state: EuchrePublicState;
  yourSeat: number | null;
  playZoneCardWidth: number;
  decoratorOf: (id: CardId) => CardDecorator;
}) {
  const p = state.players[seat]!;
  const isMe = seat === yourSeat;
  const myTeam = yourSeat !== null ? state.players[yourSeat]?.team ?? null : null;
  const isPartner = !isMe && myTeam !== null && p.team === myTeam;
  const isOpponent = !isMe && myTeam !== null && p.team !== myTeam;
  // When spectating (myTeam === null), fall back to the absolute team-0 /
  // team-1 border colors so both teams are still distinguishable.
  const teamClass = isMe
    ? "mine"
    : isPartner
    ? "partner"
    : isOpponent
    ? "opponent"
    : p.team === 0
    ? "team-0"
    : "team-1";
  const isTurn =
    state.currentSeat === seat &&
    (state.phase === "bidding1" || state.phase === "bidding2" ||
     state.phase === "discard" || state.phase === "play");
  const isDealer = seat === state.dealer;
  const isMaker = seat === state.maker;
  const trickPlay = state.currentTrickPlays.find((tp) => tp.seat === seat);
  const isWinning =
    state.currentTrickPlays.length > 0 &&
    state.currentTrickPlays[state.currentTrickWinningIndex]?.seat === seat;
  return (
    <>
      <div
        className={[
          "nameplate",
          teamClass,
          isTurn ? "turn" : "",
        ].filter(Boolean).join(" ")}
      >
        <div className="nameplate-row">
          {isMe && <span className="role-tag mine">YOU</span>}
          {isPartner && <span className="role-tag partner">PARTNER</span>}
          <span className={`conn-dot ${p.connected ? "" : "off"}`} />
          <span className="player-name">{p.displayName}</span>
          {isDealer && <span className="role-tag">DEALER</span>}
          {isMaker && <span className="role-tag">MAKER</span>}
        </div>
        <div className="nameplate-row sub">
          <span className="rank-line">won {p.tricksTaken}</span>
          {p.sittingOut && <span className="rank-line">sitting out</span>}
        </div>
      </div>
      <div className={`play-zone ${isWinning ? "winning" : ""}`}>
        {isWinning && <div className="winning-badge">WINNING</div>}
        {trickPlay && (
          <div className="play-card-wrapper" style={{ position: "relative", zIndex: 1 }}>
            <PlayingCard
              card={trickPlay.card}
              width={playZoneCardWidth}
              decorator={decoratorOf(trickPlay.card)}
            />
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Centre
// ---------------------------------------------------------------------------

function EuchreCenter({
  state,
  cardWidth,
  decoratorOf,
}: {
  state: EuchrePublicState;
  cardWidth: number;
  decoratorOf: (id: CardId) => CardDecorator;
}) {
  if (state.phase === "match_end") {
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 14 }}>
        <span className="label">Match complete</span>
        <span className="points">
          {state.teamScores[0]} – {state.teamScores[1]}
        </span>
      </div>
    );
  }
  if (state.phase === "round_end") {
    const last = state.history[state.history.length - 1];
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 14 }}>
        <span className="label">Hand complete</span>
        {last && (
          <span className="points" style={{ fontSize: 14, textAlign: "center" }}>
            {last.euchred ? "Makers euchred!" : last.march ? "March!" : ""}
            {" "}Team 0 {last.tricksByTeam[0]} · Team 1 {last.tricksByTeam[1]}
          </span>
        )}
        <span className="label">
          Score: {state.teamScores[0]} – {state.teamScores[1]} (to {state.matchTarget})
        </span>
      </div>
    );
  }
  if (state.phase === "bidding1" || state.phase === "bidding2") {
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 10, gap: 6 }}>
        <span className="label">Bidding</span>
        {state.upCard ? (
          <>
            <PlayingCard
              card={state.upCard}
              width={Math.round(cardWidth * 0.62)}
              decorator={decoratorOf(state.upCard)}
            />
            <span className="label" style={{ fontSize: 12 }}>UP CARD</span>
          </>
        ) : (
          <span className="label">
            Choose any other suit
          </span>
        )}
        <span className="label" style={{ fontSize: 11 }}>
          Score {state.teamScores[0]}–{state.teamScores[1]}
        </span>
      </div>
    );
  }
  if (state.phase === "discard") {
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 10 }}>
        <span className="label">Trump: {state.trumpSuit ? SUIT_NAME[state.trumpSuit] : "?"}</span>
        <span className="label" style={{ fontSize: 12 }}>
          Dealer is discarding…
        </span>
      </div>
    );
  }
  // Play
  return (
    <div className="center-pile" style={{ flexDirection: "column", padding: 10 }}>
      <span className="label" style={{ fontSize: 16, color: state.trumpSuit && (state.trumpSuit === "H" || state.trumpSuit === "D") ? "#c81818" : undefined }}>
        Trump {state.trumpSuit ? SUIT_GLYPH[state.trumpSuit] : "—"}
        {state.alone && " · ALONE"}
      </span>
      <span className="label" style={{ fontSize: 12 }}>
        {state.teamScores[0]}–{state.teamScores[1]} (to {state.matchTarget})
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hand progress (bottom-left widget) — analogue of Tractor's
// BottomLeftInfo. Shows trump, who's the maker, tricks taken by each
// team, and the 5-trick slot row so the player can scan the hand at
// a glance without expanding the score bar.
// ---------------------------------------------------------------------------

const TRICKS_PER_HAND = 5;

function EuchreHandProgress({
  state,
  yourSeat,
}: {
  state: EuchrePublicState;
  yourSeat: number | null;
}) {
  if (yourSeat === null || yourSeat < 0) return null;
  if (state.phase === "match_end") return null;
  // Tricks taken per team this hand.
  const tricksByTeam: [number, number] = [0, 0];
  for (const t of state.completedTricks) {
    const team = (t.winner % 2) as 0 | 1;
    tricksByTeam[team] += 1;
  }
  const slots: ({ team: 0 | 1 } | null)[] = Array.from(
    { length: TRICKS_PER_HAND },
    () => null
  );
  state.completedTricks.forEach((t, i) => {
    if (i < TRICKS_PER_HAND) slots[i] = { team: (t.winner % 2) as 0 | 1 };
  });
  const makerName =
    state.maker >= 0 ? state.players[state.maker]?.displayName ?? "?" : null;
  const makerTeam = state.makerTeam === 0 || state.makerTeam === 1 ? state.makerTeam : null;

  return (
    <div className="board-info-bottom-left euchre-hand-progress">
      <div className="board-info-line">
        <span className="label">Tricks</span>
        <div className="euchre-trick-slots">
          {slots.map((s, i) => (
            <div
              key={i}
              className={`euchre-trick-slot ${s ? "filled" : "empty"}`}
              style={s ? { background: teamColor(s.team), borderColor: teamColor(s.team) } : undefined}
              title={
                s
                  ? `Trick ${i + 1}: won by ${teamName(s.team)}`
                  : `Trick ${i + 1}: not yet played`
              }
            >
              {s ? teamName(s.team)[0] : i + 1}
            </div>
          ))}
        </div>
      </div>
      <div className="board-info-line">
        <span className="label">{teamName(0)}</span>
        <span className="value" style={{ color: teamColor(0) }}>{tricksByTeam[0]}</span>
        <span className="label" style={{ marginLeft: 8 }}>{teamName(1)}</span>
        <span className="value" style={{ color: teamColor(1) }}>{tricksByTeam[1]}</span>
      </div>
      {makerName && makerTeam !== null && (
        <div className="board-info-line">
          <span className="label">Maker</span>
          <span className="value" style={{ color: teamColor(makerTeam) }}>
            {makerName}{state.alone ? " · alone" : ""}
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Score panel
// ---------------------------------------------------------------------------

function EuchreScorePanel({
  state,
  yourSeat,
}: {
  state: EuchrePublicState;
  yourSeat: number | null;
}) {
  const team0 = state.players.filter((p) => p.team === 0).map((p) => p.displayName).join(" + ");
  const team1 = state.players.filter((p) => p.team === 1).map((p) => p.displayName).join(" + ");
  const suitGlyph: Record<string, string> = { C: "♣", D: "♦", H: "♥", S: "♠" };
  return (
    <div className="hearts-score-panel euchre-history-panel">
      <div className="hearts-score-header">
        <span className="muted">Hand history</span>
      </div>
      <table className="hearts-score-table">
        <thead>
          <tr>
            <th>Team</th>
            <th style={{ textAlign: "right" }}>Score (to {state.matchTarget})</th>
          </tr>
        </thead>
        <tbody>
          <tr className={yourSeat !== null && yourSeat % 2 === 0 ? "you" : ""}>
            <td>
              <span style={{ color: teamColor(0) }}>{teamName(0)}</span>: {team0}
            </td>
            <td style={{ textAlign: "right" }}>{state.teamScores[0]}</td>
          </tr>
          <tr className={yourSeat !== null && yourSeat % 2 === 1 ? "you" : ""}>
            <td>
              <span style={{ color: teamColor(1) }}>{teamName(1)}</span>: {team1}
            </td>
            <td style={{ textAlign: "right" }}>{state.teamScores[1]}</td>
          </tr>
        </tbody>
      </table>
      {state.history.length > 0 && (
        <>
          <div className="muted" style={{ fontSize: 11, marginTop: 10, letterSpacing: 0.5 }}>
            RECENT HANDS
          </div>
          <table className="hearts-score-table" style={{ marginTop: 4 }}>
            <thead>
              <tr>
                <th>#</th>
                <th>Maker</th>
                <th style={{ textAlign: "center" }}>Tricks</th>
                <th style={{ textAlign: "right" }}>Result</th>
              </tr>
            </thead>
            <tbody>
              {state.history.slice(-8).map((h) => {
                const makerName = state.players[h.maker]?.displayName ?? "?";
                const winningTeam = h.euchred ? ((1 - h.makerTeam) as 0 | 1) : h.makerTeam;
                const pts = h.pointsByTeam[winningTeam];
                const suffix = h.euchred ? " euchred" : h.march ? " march" : "";
                return (
                  <tr key={h.round}>
                    <td>{h.round}</td>
                    <td>
                      {makerName}{" "}
                      <span style={{ color: h.trumpSuit === "H" || h.trumpSuit === "D" ? "#c81818" : undefined }}>
                        {suitGlyph[h.trumpSuit] ?? ""}
                      </span>
                      {h.alone && " ·alone"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {h.tricksByTeam[0]}–{h.tricksByTeam[1]}
                    </td>
                    <td style={{ textAlign: "right", color: teamColor(winningTeam) }}>
                      {teamName(winningTeam)} +{pts}{suffix}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action bars
// ---------------------------------------------------------------------------

function BidActionBar({
  state,
  yourSeat,
  send,
}: {
  state: EuchrePublicState;
  yourSeat: number;
  send: (m: ClientMsg) => void;
}) {
  const round1 = state.phase === "bidding1";
  const upSuit = state.upCard ? (state.upCard[1] as SuitId) : null;
  const mustCall = state.phase === "bidding2" && state.stickTheDealer && yourSeat === state.dealer;
  return (
    <div className="action-bar" style={{ flexWrap: "wrap", justifyContent: "center", gap: 6 }}>
      {round1 ? (
        <>
          <button
            onClick={() =>
              send({ t: "action", action: { kind: "euchre", payload: { type: "bid_order_up", alone: false } } })
            }
          >
            Order up {upSuit ? SUIT_GLYPH[upSuit] : ""}
          </button>
          <button
            onClick={() =>
              send({ t: "action", action: { kind: "euchre", payload: { type: "bid_order_up", alone: true } } })
            }
            title="Your partner sits out this hand. +4 if you take all 5 tricks."
          >
            Alone {upSuit ? SUIT_GLYPH[upSuit] : ""}
          </button>
          <button
            onClick={() =>
              send({ t: "action", action: { kind: "euchre", payload: { type: "bid_pass" } } })
            }
          >
            Pass
          </button>
        </>
      ) : (
        <>
          {(["C", "D", "H", "S"] as const).map((s) => {
            const disabled = upSuit !== null && s === upSuit;
            return (
              <button
                key={s}
                disabled={disabled}
                title={disabled ? "Can't call the turned-down suit" : ""}
                onClick={() =>
                  send({ t: "action", action: { kind: "euchre", payload: { type: "bid_call", suit: s, alone: false } } })
                }
                style={{ color: s === "H" || s === "D" ? "#c81818" : undefined }}
              >
                Call {SUIT_GLYPH[s]}
              </button>
            );
          })}
          {(["C", "D", "H", "S"] as const).map((s) => {
            const disabled = upSuit !== null && s === upSuit;
            return (
              <button
                key={`alone-${s}`}
                disabled={disabled}
                title={disabled ? "Can't call the turned-down suit" : "Partner sits out this hand."}
                onClick={() =>
                  send({ t: "action", action: { kind: "euchre", payload: { type: "bid_call", suit: s, alone: true } } })
                }
                style={{ color: s === "H" || s === "D" ? "#c81818" : undefined }}
              >
                Alone {SUIT_GLYPH[s]}
              </button>
            );
          })}
          <button
            disabled={mustCall}
            title={mustCall ? "Stick the dealer — you must call." : ""}
            onClick={() =>
              send({ t: "action", action: { kind: "euchre", payload: { type: "bid_pass" } } })
            }
          >
            Pass
          </button>
        </>
      )}
    </div>
  );
}

function DiscardActionBar({
  selectedCard,
  send,
  onAfterSubmit,
}: {
  selectedCard: CardId | null;
  send: (m: ClientMsg) => void;
  onAfterSubmit: () => void;
}) {
  return (
    <div className="action-bar">
      <button
        className="primary"
        disabled={!selectedCard}
        onClick={() => {
          if (!selectedCard) return;
          send({
            t: "action",
            action: { kind: "euchre", payload: { type: "dealer_discard", card: selectedCard } },
          });
          onAfterSubmit();
        }}
      >
        Discard
      </button>
    </div>
  );
}

function PlayActionBar({
  selectedCard,
  send,
  onAfterSubmit,
}: {
  selectedCard: CardId | null;
  send: (m: ClientMsg) => void;
  onAfterSubmit: () => void;
}) {
  return (
    <div className="action-bar">
      <button
        className="primary"
        disabled={!selectedCard}
        onClick={() => {
          if (!selectedCard) return;
          send({
            t: "action",
            action: { kind: "euchre", payload: { type: "play", card: selectedCard } },
          });
          onAfterSubmit();
        }}
      >
        Play card
      </button>
    </div>
  );
}

function ReadyActionBar({ send }: { send: (m: ClientMsg) => void }) {
  return (
    <div className="action-bar">
      <button
        className="primary"
        onClick={() =>
          send({ t: "action", action: { kind: "euchre", payload: { type: "ready_next_round" } } })
        }
      >
        Ready for next hand
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legality hint — must follow suit, with left bower counting as trump.
// ---------------------------------------------------------------------------

function effectiveSuitId(card: CardId, trump: SuitId | null): string {
  if (card.length !== 2) return card[1] ?? "";
  const rank = card[0];
  const suit = card[1];
  if (trump && rank === "J" && suit === SAME_COLOR[trump]) return trump;
  return suit ?? "";
}

function computeEuchreLegal(
  hand: CardId[],
  state: EuchrePublicState
): Set<CardId> {
  const out = new Set<CardId>();
  const trick = state.currentTrickPlays;
  const trump = state.trumpSuit;
  if (trick.length === 0) {
    for (const c of hand) out.add(c);
    return out;
  }
  const ledEff = effectiveSuitId(trick[0]!.card, trump);
  const inSuit = hand.filter((c) => effectiveSuitId(c, trump) === ledEff);
  if (inSuit.length > 0) {
    for (const c of inSuit) out.add(c);
  } else {
    for (const c of hand) out.add(c);
  }
  return out;
}
